export type FigureDiagramSpec = {
  imageType?: string;
  description?: string;
  figureRole?: string;
  /**
   * Raster image briefs: `content_json.description` when present.
   * Shown in session debug mode.
   */
  contentDescription?: string;
  /**
   * Full extracted `content_json` (pretty-printed when valid JSON).
   * For vector types like step_process this is the extracted brief (title/steps/etc.).
   * Shown in session debug mode.
   */
  contentJson?: string;
};

function readSpecField(spec: string, field: string): string | undefined {
  // Same-line only — empty values must not capture the next field name (e.g. content_json:).
  const match = spec.match(new RegExp(`^${field}:\\s*([^\\r\\n]*)$`, 'im'));
  const value = match?.[1]?.trim();
  return value || undefined;
}

function readContentJsonRaw(spec: string): string | undefined {
  const marker = spec.match(/content_json:\s*/i);
  if (!marker || marker.index == null) return undefined;
  const start = marker.index + marker[0].length;
  const end = spec.indexOf('[/DIAGRAM_SPEC]', start);
  const raw = (end >= 0 ? spec.slice(start, end) : spec.slice(start)).trim();
  return raw || undefined;
}

function readContentJsonFields(spec: string): {
  contentDescription?: string;
  contentJson?: string;
} {
  const raw = readContentJsonRaw(spec);
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw) as { description?: unknown };
    const description =
      typeof parsed?.description === 'string' ? parsed.description.trim() : '';
    return {
      contentDescription: description || undefined,
      contentJson: JSON.stringify(parsed, null, 2),
    };
  } catch {
    return { contentJson: raw };
  }
}

/** Parse typed figure metadata from the hidden FIGURE_DIAGRAM_SPEC comment body. */
export function parseFigureDiagramSpec(spec: string | undefined): FigureDiagramSpec | null {
  if (!spec?.trim()) return null;

  const imageType = readSpecField(spec, 'image_type');
  const description = readSpecField(spec, 'description');
  const figureRole = readSpecField(spec, 'figure_role');
  const { contentDescription, contentJson } = readContentJsonFields(spec);

  if (!imageType && !description && !figureRole && !contentDescription && !contentJson) {
    return null;
  }

  return { imageType, description, figureRole, contentDescription, contentJson };
}

/** Short caption for tooltips / aria-label (image_focus sent to the model). */
export function figureDiagramAriaLabel(spec: FigureDiagramSpec | null, figureKey: string): string {
  if (spec?.description) return spec.description;
  if (spec?.imageType) return `${figureKey}: ${spec.imageType}`;
  return figureKey;
}
